// The adjustment formula for the print review preview: three 256-entry lookup
// tables (R, G, B). A copy of PoseAdjustments::ToneCurve (Ruby) and of the
// EC2 renderer's version -- read the formula and the reason for three copies
// there. poseAdjustmentToneCurve.test.js asserts this copy against the shared
// fixture, spec/fixtures/files/pose_adjustment_tone_curves.json.

// Breaks .5 rounding ties the same way in Ruby, JS and Python; see the Ruby copy.
const TIE_NUDGE = 1e-6;
// How far shadows / highlights at +-100 move their tone; see the Ruby copy.
const TONE_STRENGTH = 0.12;
// How far the bump S-curve moves the quarter and three-quarter tones, in bytes.
const BUMP_POINTS = 6;
// Rec. 709 luma, the grey saturation mixes towards.
const LUMA = [0.2126, 0.7152, 0.0722];

const decode = (value) => (value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4);

const encode = (value) => (value <= 0.0031308 ? value * 12.92 : 1.055 * value ** (1 / 2.4) - 0.055);

const tone = (value, shadowAmount, highlightAmount) => (
  value
  + shadowAmount * 6.75 * value * (1 - value) ** 2
  + highlightAmount * 6.75 * value ** 2 * (1 - value)
);

// The fixed S: 0 at black, mid-grey and white, -6/255 at the quarter tone and
// +6/255 at the three-quarter tone.
const bumpCurve = (value) => value - (BUMP_POINTS / 255) * Math.sin(2 * Math.PI * value);

// saturation is accepted and ignored here: it mixes the channels, so it cannot
// be a table. Apply it to the pixels afterwards with `saturatePixels`.
const lookupTables = ({
  temperature = 0, tint = 0, exposure = 0, contrast = 0, highlights = 0, shadows = 0, bump = false,
} = {}) => {
  const t = temperature / 100;
  const n = tint / 100;
  const exposureGain = 2 ** exposure;
  const slope = 1 + contrast / 100;
  const shadowAmount = (TONE_STRENGTH * shadows) / 100;
  const highlightAmount = (TONE_STRENGTH * highlights) / 100;
  const gains = [2 ** (0.35 * t + 0.10 * n), 2 ** (-0.20 * n), 2 ** (-0.35 * t + 0.10 * n)];

  return gains.map((gain) => {
    const table = new Uint8Array(256);
    for (let i = 0; i < 256; i += 1) {
      const linear = Math.min(decode(i / 255) * gain * exposureGain, 1);
      let y = (tone(encode(linear), shadowAmount, highlightAmount) - 0.5) * slope + 0.5;
      if (bump) y = bumpCurve(Math.min(Math.max(y, 0), 1));
      table[i] = Math.floor(Math.min(Math.max(y, 0), 1) * 255 + 0.5 + TIE_NUDGE);
    }
    return table;
  });
};

// The second stage, over RGBA bytes in place (canvas ImageData order), after the
// tables: mix each pixel towards its own luma. Alpha is left alone.
export const saturatePixels = (data, saturation) => {
  if (!saturation) return data;
  const factor = 1 + saturation / 100;

  for (let i = 0; i < data.length; i += 4) {
    const luma = LUMA[0] * data[i] + LUMA[1] * data[i + 1] + LUMA[2] * data[i + 2];
    for (let c = 0; c < 3; c += 1) {
      const value = luma + factor * (data[i + c] - luma);
      data[i + c] = Math.floor(Math.min(Math.max(value, 0), 255) + 0.5 + TIE_NUDGE);
    }
  }
  return data;
};

export default lookupTables;
